/**
 * UI-side mirror of `base/src/core/services/forecastAccess.ts` — MUST stay
 * in sync (the release PR drops the softDeploy clauses from both files and
 * clears the forecast `featureFlag` entries in ai/agent/tools/registry.ts).
 */

import store from 'Store/index';
import { Feature } from 'Store/actions/FeatureHelperActions';
import {
	getFeatureAvailableFromState,
	getSoftDeployAccessFromState,
	useFeatureAvailable
} from 'App/components/hooks/featureHelper';
import { useSoftDeployAccess } from 'App/components/hooks';

// License = tier feature OR the legacy AI_ASSISTANT soft-deploy escape hatch.
const hasLicense = (): boolean => {
	const accountSelf = store.getState().App.accountSelf;
	return (
		getFeatureAvailableFromState(accountSelf, Feature.AI_ASSISTANT) ||
		getSoftDeployAccessFromState(accountSelf, 'AI_ASSISTANT')
	);
};

// Plain function — usable from Angular controllers (editOrder), non-React
// utilities, anywhere outside a React render. Reads the current redux state.
export const hasDealForecastAccess = (): boolean =>
	hasLicense() && getSoftDeployAccessFromState(store.getState().App.accountSelf, 'DEAL_FORECASTING');

// React-hook variants. Each hook MUST be called unconditionally (no inline
// `&&` between hook calls): accountSelf arrives async, and a short-circuited
// hook flips the hook count between renders.
export const useDealForecastAccess = (): boolean => {
	const hasTierLicense = useFeatureAvailable(Feature.AI_ASSISTANT);
	const hasSoftDeployLicense = useSoftDeployAccess('AI_ASSISTANT');
	const hasFlag = useSoftDeployAccess('DEAL_FORECASTING');
	return (hasTierLicense || hasSoftDeployLicense) && hasFlag;
};

export const usePipelineForecastAccess = (): boolean => {
	const hasTierLicense = useFeatureAvailable(Feature.AI_ASSISTANT);
	const hasSoftDeployLicense = useSoftDeployAccess('AI_ASSISTANT');
	const hasFlag = useSoftDeployAccess('PIPELINE_FORECAST');
	return (hasTierLicense || hasSoftDeployLicense) && hasFlag;
};
